/* eslint-disable max-lines */
import { ConnectedPosition, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  afterRenderEffect,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DoCheck,
  effect,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators,
  type ValidationErrors,
  type Validator,
} from '@angular/forms';
import { HxFocusChangeEvent, HxValueChangeEvent } from '../../base/events';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxControlSize } from '../../base/types';
import { createValidationState, ValidationState } from '../../base/validation-state';
import { HxError, HxFormGroup, HxHint } from '../form-group/index';
import { HxIconButton } from '../icon-button/icon-button';
import { HxLabel } from '../label/label';
import { PopoverPosition } from '../popover';
import {
  DICTATION_ENABLED_CHANGED_EVENT,
  DictationState,
  HxDictationController,
  isDictationFeatureEnabled,
} from '../textarea/dictation';

import { EllipsisTooltipDirective } from '../../directives';
import { HxIcon } from '../icon/icon';

/**
 * HTML5 input type attribute values supported by HxInput.
 *
 * Focused on text-based data entry. For specialized inputs, use:
 *
 * - HxCheckbox for checkbox inputs
 * - HxRadio/HxRadioGroup for radio inputs
 * - HxDatePicker for date inputs
 * - HxSearchTypeahead for search with autocomplete
 */
export type InputType =
  'text' | 'email' | 'password' | 'tel' | 'url' | 'number' | 'search' | 'date' | 'time' | 'timeRange';

/**
 * Visual style variant.
 *
 * @see contracts/input-types.ts
 */
export type InputVariant = 'fill' | 'outline' | 'borderless';

/**
 * Component size - extracted from HxControlSize.
 *
 * @see contracts/input-types.ts
 */
export type InputSize = Extract<HxControlSize, 'sm' | 'md' | 'lg' | 'xl'>;

/**
 * Component width size.
 *
 * Maps to specific width percentages:
 *
 * - ExtraSmall: 10%
 * - Small: 20%
 * - Medium: 40%
 * - Large: 60%
 * - ExtraLarge: 80%
 * - Full: 100%
 */
export type WidthSize = 'extraSmall' | 'small' | 'medium' | 'large' | 'extraLarge' | 'full';

/** Label/Input orientation. */
export type InputOrientation = 'vertical' | 'horizontal';

/**
 * Autocomplete attribute values.
 *
 * @see contracts/input-types.ts
 */
export type AutocompleteType =
  | 'off'
  | 'on'
  | 'name'
  | 'given-name'
  | 'family-name'
  | 'email'
  | 'username'
  | 'new-password'
  | 'current-password'
  | 'tel'
  | 'tel-national'
  | 'street-address'
  | 'address-line1'
  | 'address-line2'
  | 'postal-code'
  | 'country'
  | 'bday'
  | 'url';

// --- Event Interfaces (T012) ---

/**
 * Event emitted when input value changes. Extends HxValueChangeEvent with string type.
 *
 * @see contracts/input-events.ts
 */
export interface InputChangeEvent extends HxValueChangeEvent<string> {
  source: HxInput;
}

/**
 * Event emitted when input gains or loses focus. Extends HxFocusChangeEvent with source reference.
 *
 * @see contracts/input-events.ts
 */
export interface InputFocusEvent extends HxFocusChangeEvent {
  source: HxInput;
}

/**
 * HxInput Component
 *
 * Single-line text input component for the Harmony design system. Extends HxBaseControl and implements Validator for
 * Angular Forms integration.
 *
 * @see specs/012-harmony-input/spec.md
 * @see specs/012-harmony-input/data-model.md
 * @see specs/012-harmony-input/contracts/input-api.ts
 */
@Component({
  selector: 'hx-input',
  standalone: true,
  imports: [CommonModule, HxIcon, HxFormGroup, HxLabel, HxHint, HxError, EllipsisTooltipDirective, HxIconButton],
  templateUrl: './input.html',
  styleUrls: ['./input-tokens.css', './input.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HxInput),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => HxInput),
      multi: true,
    },
  ],

  host: {
    '[class]': 'hostClasses()',
    '[attr.id]': 'id()',
    '[class.hx-input-picker-open]': '_timeRangePickerOpen()',
  },
  encapsulation: ViewEncapsulation.None,
})
export class HxInput extends HxBaseControl implements Validator, AfterViewInit, DoCheck, OnDestroy {
  private readonly _hostElement = inject(ElementRef<HTMLElement>);
  private readonly _overlay = inject(Overlay);
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private _overlayRef: OverlayRef | null = null;

  // --- Component Inputs ---

  /** Input type */
  readonly type = input<InputType>('text');

  /** Visual variant */
  readonly variant = input<InputVariant>('outline');

  /** Component size */
  readonly size = input<InputSize>('md');

  /** Read-only state */
  readonly readonly = input<boolean>(false);

  /** Value */
  readonly valueInput = input<string | undefined>(undefined, { alias: 'value' });

  /** Required state */
  readonly requiredInput = input<boolean>(false, { alias: 'required' });

  /** Pattern validation */
  readonly pattern = input<string | undefined>(undefined);

  /** Minimum length */
  readonly minlength = input<number | undefined>(undefined);

  /** Maximum length */
  readonly maxlength = input<number | undefined>(undefined);

  /** Minimum value (for number/date/time) - Defaults to safe JavaScript limits */
  readonly min = input<string | number | undefined>(undefined);

  /** Maximum value (for number/date/time) - Defaults to safe JavaScript limits */
  readonly max = input<string | number | undefined>(undefined);

  /** Computed minimum value with intelligent defaults based on input type */
  protected readonly _effectiveMin = computed(() => {
    const minValue = this.min();
    if (minValue !== undefined && minValue !== null) return minValue;

    return Number.MIN_SAFE_INTEGER;
  });

  /** Computed maximum value with intelligent defaults based on input type */
  protected readonly _effectiveMax = computed(() => {
    const maxValue = this.max();
    if (maxValue !== undefined && maxValue !== null) return maxValue;

    return Number.MAX_SAFE_INTEGER;
  });

  /** Whether negative values are allowed (number type only). When false, only values >= 0 are permitted. */
  readonly allowNegatives = input<boolean>(false);

  /** Autocomplete attribute */
  readonly autocomplete = input<AutocompleteType | undefined>(undefined);

  /** Autofocus attribute */
  readonly autofocus = input<boolean>(false);

  /** ARIA label */
  readonly ariaLabelInput = input<string | undefined>(undefined, { alias: 'ariaLabel' });

  /** Start icon name */
  readonly startIcon = input<string | undefined>(undefined);

  /** End icon name */
  readonly endIcon = input<string | undefined>(undefined);

  /** Label subtitle text displayed below the label and above the input */
  readonly subtitle = input<string>('');

  /** Hint text displayed below the input */
  readonly hint = input<string>('');

  /** Whether to suppress rendering the internal error message template */
  readonly hideErrorMessage = input<boolean>(false);

  /** Position for the tooltip shown when the input value is truncated. */
  readonly ellipsisTooltipPosition = input<PopoverPosition>('top');

  /** Custom error message for required validation */
  readonly requiredErrorMessage = input<string>('');

  /** Custom error message for minlength validation */
  readonly minlengthErrorMessage = input<string>('');

  /** Custom error message for maxlength validation */
  readonly maxlengthErrorMessage = input<string>('');

  /** Custom error message for pattern validation */
  readonly patternErrorMessage = input<string>('');

  /** Custom error message for min validation */
  readonly minErrorMessage = input<string>('');

  /** Custom error message for max validation */
  readonly maxErrorMessage = input<string>('');

  /** Custom error message for negative value validation */
  readonly negativeValueErrorMessage = input<string>('');

  /** Custom error message for email validation */
  readonly emailErrorMessage = input<string>('');

  /** Custom error message for alphabetical validation */
  readonly alphabeticalErrorMessage = input<string>('');

  /** Custom error message for numerical validation */
  readonly numericalErrorMessage = input<string>('');

  /** Custom error message for special character validation */
  readonly specialCharacterErrorMessage = input<string>('');

  /** Custom error message for custom validation */
  readonly customErrorMessage = input<string>('');

  /** Custom error message for time range validation */
  readonly timeRangeErrorMessage = input<string>('');

  /** Multi-line text area mode */
  readonly multiline = input<boolean>(false);

  /** Auto-expand height to fit content */
  readonly autoExpand = input<boolean>(false);

  /** Number of visible text lines for textarea */
  readonly rows = input<number>(3);

  /** Minimum number of rows when auto-expand is enabled */
  readonly minRows = input<number>(3);

  /** Maximum number of rows when auto-expand is enabled */
  readonly maxRows = input<number | undefined>(undefined);

  /** Textarea resize behavior */
  readonly resize = input<'none' | 'vertical' | 'horizontal' | 'both'>('vertical');

  /** Horizontal alignment of input container (left, center, right) */
  readonly justify = input<'left' | 'center' | 'right'>('left');

  /** Width size of the component */
  readonly widthSize = input<WidthSize>('full');

  /** Layout orientation for label and input */
  readonly orientation = input<InputOrientation>('vertical');

  // ============================================================================
  // INTERNAL STATE
  // ============================================================================

  /** Internal FormControl for managing input state */
  protected _internalControl = new FormControl<string>('', { nonNullable: true });

  /** Internal signal tracking the current value */
  protected readonly _internalValue = signal<string>('');

  /** Computed signal exposing current value for template binding */
  protected readonly currentValue = computed(() => this._internalValue());

  /** Signal for password visibility toggle */
  protected _showPassword = signal<boolean>(false);

  /** Computed signal for the actual input type (handles password toggle) */
  protected _inputType = computed(() => {
    if (this.type() === 'password' && this._showPassword()) {
      return 'text';
    }
    if (this.type() === 'timeRange') {
      return 'text';
    }
    return this.type();
  });

  /** Computed signal for the password toggle icon */
  protected _passwordToggleIcon = computed(() => {
    return this._showPassword() ? 'eye-slash' : 'eye';
  });

  /** Resolve the effective trailing icon. Returns the explicit endIcon input if provided. */
  protected readonly _resolvedEndIcon = computed(() => {
    const explicitEndIcon = this.endIcon();
    if (explicitEndIcon) {
      return explicitEndIcon;
    }

    return undefined;
  });

  /**
   * Unified validation state - SINGLE SOURCE OF TRUTH for all validation display logic Replaces the previous scattered
   * signals (_invalid, _touched, _dirty) and getters (errorState, currentControlErrors)
   */
  protected _validationState!: ValidationState;

  /**
   * Local signal that mirrors showErrors() for reactive host class binding. This signal is updated whenever validation
   * state changes, ensuring the host class binding updates synchronously with the template.
   */
  protected readonly _showErrors = signal<boolean>(false);

  /**
   * Tracks the previous touched state of external control. Used in DoCheck to detect programmatic touch changes (e.g.,
   * form.markAllAsTouched())
   */
  private _previousExternalTouched = false;

  // --- Outputs ---

  /** Emitted when value changes */
  readonly valueChange = output<InputChangeEvent>();

  /** Emitted when focus state changes */
  readonly focusChange = output<InputFocusEvent>();

  // --- ViewChild References ---

  /** Reference to the input element */
  private readonly _inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');

  /** Reference to the textarea element */
  private readonly _textareaElement = viewChild<ElementRef<HTMLTextAreaElement>>('textareaElement');

  /** Emitted when input loses focus */
  readonly blurred = output<void>();

  /** Controls the time range picker popup visibility for timeRange mode. */
  protected readonly _timeRangePickerOpen = signal<boolean>(false);

  /** Template ref for the time range picker popup (rendered via CDK Overlay into document.body). */
  private readonly _timeRangePickerTemplate = viewChild<TemplateRef<unknown>>('timeRangePickerTpl');

  /** Start time value for time range picker popup (24-hour HH:mm). */
  protected readonly _timeRangeStart = signal<string>('');

  /** End time value for time range picker popup (24-hour HH:mm). */
  protected readonly _timeRangeEnd = signal<string>('');

  /**
   * Controls whether the dictation (speech-to-text) microphone icon may be shown on multiline inputs. Defaults to
   * `undefined`, meaning the component defers to the `dictationEnabled` local-storage feature flag. Only applies when
   * `multiline` is true.
   */
  readonly dictationEnabled = input<boolean | undefined>(undefined);

  /** Whether the current browser supports the Web Speech API used for dictation. */
  protected readonly _dictationSupported = HxDictationController.isSupported();

  /** Live-updating local-storage dictation feature flag (kept in sync via storage/custom events). */
  private readonly _dictationFeatureFlag = signal(isDictationFeatureEnabled());

  /** Whether the dictation microphone icon should be rendered. */
  readonly showDictation = computed(
    () => this.multiline() && (this.dictationEnabled() ?? this._dictationFeatureFlag()) && this._dictationSupported
  );

  /** Current dictation lifecycle state: idle, listening, or error. */
  readonly dictationState = signal<DictationState>('idle');

  /** Accessible label/tooltip for the dictation button, reflecting the current state. */
  protected readonly dictationButtonLabel = computed(() =>
    this.dictationState() === 'listening' ? 'Stop dictation' : 'Start dictation'
  );

  private _dictationController: HxDictationController | null = null;

  /** Textarea value captured the moment the current dictation session started. */
  private _dictationBaseValue = '';

  /** Finalized transcript text committed so far during the current dictation session. */
  private _dictationCommittedText = '';

  private readonly _refreshDictationFeatureFlag = (): void => {
    this._dictationFeatureFlag.set(isDictationFeatureEnabled());
    this._changeDetectorRef.markForCheck();
  };

  // --- Computed Properties ---

  /**
   * Gets the CSS classes for the host element. Uses _showErrors signal for reactive updates.
   *
   * @internal
   */
  protected readonly hostClasses = computed(() => {
    const classes = ['hx-input'];

    // Variant
    classes.push(`hx-input-${this.variant()}`);

    // Size
    classes.push(`hx-input-${this.size()}`);

    // State
    if (this.disabledState()) {
      classes.push('hx-input-disabled');
    }
    if (this.readonly()) {
      classes.push('hx-input-readonly');
    }
    // Use reactive _showErrors signal for synchronous updates with template
    if (this._showErrors()) {
      classes.push('hx-input-invalid');
    }

    // Multiline
    if (this.multiline()) {
      classes.push('hx-input-multiline');
      if (this.autoExpand()) {
        classes.push('hx-input-auto-expand');
      }
    }

    // Justification
    classes.push(`hx-input-${this.justify()}`);

    // Width size
    classes.push(`hx-input-width-${this.widthSize()}`);

    return classes.join(' ');
  });

  constructor() {
    super();

    // Initialize unified validation state - SINGLE SOURCE OF TRUTH
    this._validationState = createValidationState({
      internalControl: this._internalControl,
      getNgControl: () => this.ngControl,
    });

    // Manually register this instance as the value accessor and validator
    // This breaks the DI cycle caused by the NG_VALUE_ACCESSOR token lookup.
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    // Sync FormControl validators when inputs change
    effect(() => {
      const control = this._internalControl;
      const validators = this._buildValidators();
      control.setValidators(validators);
      control.updateValueAndValidity({ emitEvent: false });

      // Update validation state after validator changes
      this._validationState.updateAfterValueChange();

      this._validatorChangeFn();
    });

    // Allow external value binding via [value] when the component is not using Angular forms.
    // `_internalValue` is read untracked so this effect only reacts to `valueInput()` changes -
    // otherwise typing (which updates `_internalValue` via `_handleInput`) would re-trigger this
    // effect and reset the in-progress keystrokes back to the still-stale (debounced) external value.
    effect(() => {
      const externalValue = this.valueInput();
      if (externalValue === undefined) {
        return;
      }

      const normalizedValue = externalValue ?? '';
      if (untracked(() => this._internalValue()) === normalizedValue) {
        return;
      }

      this._internalValue.set(normalizedValue);
      this._internalControl.setValue(normalizedValue, { emitEvent: false });
      this._validationState.updateAfterValueChange();
    });

    // Watch for autofocus changes and react accordingly
    effect(() => {
      if (this.autofocus()) {
        this.focus();
      }
    });

    // Keep popup time values in sync when value is written externally.
    effect(() => {
      if (this.type() !== 'timeRange') {
        return;
      }

      const parsed = this._parseTimeRange(this._internalValue());
      if (!parsed) {
        this._timeRangeStart.set('');
        this._timeRangeEnd.set('');
        return;
      }

      this._timeRangeStart.set(this._minutesToTwentyFourHour(parsed.startMinutes));
      this._timeRangeEnd.set(this._minutesToTwentyFourHour(parsed.endMinutes));
    });

    // Push `_internalValue` onto the native element only when it actually differs from what the element already
    // holds. A plain `[value]` binding re-assigns on every change, which resets the caret to position 0 while the
    // user is still typing (e.g. deleting digits from "15.345": the partial entry "15." sanitizes to '' on a
    // number input, so the bound value changes even though the displayed text does not).
    afterRenderEffect(() => {
      const value = this._internalValue();
      const element = (this.multiline() ? this._textareaElement() : this._inputElement())?.nativeElement;
      if (element && element.value !== value) {
        element.value = value;
      }
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    // Sync initial state from external control if available
    this._validationState.syncFromExternalControl();
    this._syncShowErrors();

    // Subscribe to external control changes to sync _showErrors signal
    // This handles programmatic changes like form.markAllAsTouched()
    const externalControl = this.ngControl?.control;
    if (externalControl) {
      externalControl.statusChanges?.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
        this._syncShowErrors();
        this._changeDetectorRef.markForCheck();
      });
    }
  }

  ngAfterViewInit(): void {
    // Handle autofocus after the view is initialized
    if (this.autofocus()) {
      this.focus();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener(DICTATION_ENABLED_CHANGED_EVENT, this._refreshDictationFeatureFlag);
      window.addEventListener('storage', this._refreshDictationFeatureFlag);
    }
  }

  /**
   * DoCheck lifecycle hook to detect programmatic touch state changes. This is critical for handling
   * form.markAllAsTouched() which doesn't trigger statusChanges.
   */
  ngDoCheck(): void {
    const externalControl = this.ngControl?.control;
    if (externalControl) {
      const currentTouched = externalControl.touched;
      // If touched state changed externally (e.g., form.markAllAsTouched())
      if (currentTouched !== this._previousExternalTouched) {
        this._previousExternalTouched = currentTouched;
        this._syncShowErrors();
      }
    }
  }

  // ============================================================================
  // COMPUTED GETTERS
  // ============================================================================
  // --- ControlValueAccessor Implementation ---

  override writeValue(value: any): void {
    super.writeValue(value);
    const normalizedValue = value ?? '';

    this._internalValue.set(normalizedValue);
    this._internalControl.setValue(normalizedValue, { emitEvent: false });
    this._validationState.updateAfterValueChange();
    this._syncShowErrors();
  }

  override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
    isDisabled ? this._internalControl.disable() : this._internalControl.enable();
    // Template [disabled] binding handles element state automatically
  }

  // --- Event Handlers ---

  /** Handle input event */
  _handleInput(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const previousValue = this._internalValue();

    // Enforce maxlength programmatically so DOM manipulation cannot bypass the limit
    const maxLen = this.maxlength();
    let newValue = target.value;
    if (maxLen !== undefined && newValue.length > maxLen) {
      newValue = newValue.slice(0, maxLen);
      target.value = newValue; // Correct the DOM element value as well
    }

    // 1. Update internal state
    this._internalValue.set(newValue);
    this._internalControl.setValue(newValue);

    // 2. Notify external control FIRST (critical for proper sync)
    this._onChange(newValue);

    // 3. Mark as dirty and update validation state AFTER external control notified
    this._validationState.markDirty();
    this._syncShowErrors();

    // Handle auto-expand height calculation
    if (this.multiline() && this.autoExpand() && target.tagName === 'TEXTAREA') {
      this._calculateHeight(target as HTMLTextAreaElement);
    }

    this.valueChange.emit({
      value: newValue,
      previousValue: previousValue,
      source: this,
    });

    // Manual edits (typing/deleting) while dictation is listening must not be clobbered by the next
    // interim/final speech event, which otherwise recomposes from the stale base captured at session start.
    if (this.dictationState() === 'listening') {
      this._dictationBaseValue = newValue;
      this._dictationCommittedText = '';
    }
  }

  /** Handle focus event */
  _handleFocus(): void {
    this.focusChange.emit({ focused: true, source: this });
  }

  /** Handle blur event */
  _handleBlur(): void {
    // Trim whitespace for required text inputs on blur
    if (this.type() === 'text' && this.requiredInput()) {
      const currentValue = this._internalValue();
      const trimmedValue = typeof currentValue === 'string' ? currentValue.trim() : currentValue;

      if (currentValue !== trimmedValue) {
        this._internalValue.set(trimmedValue);
        this._internalControl.setValue(trimmedValue);
        this._onChange(trimmedValue);
      }
    }

    // Mark as touched and update validation state
    this._validationState.markTouched();
    this._syncShowErrors();
    this._onTouched();
    this.blurred.emit();
    this.focusChange.emit({ focused: false, source: this });
  }

  _handleTimeRangeFieldClick(event: Event): void {
    if (this.type() !== 'timeRange' || this.disabledState() || this.readonly()) {
      return;
    }

    event.preventDefault();
    if (!this._timeRangePickerOpen()) {
      this._openTimeRangePicker();
    }
  }

  /** Native date/time inputs do not render placeholder text, so we draw a visual placeholder when empty. */
  protected readonly _showNativePickerPlaceholder = computed(() => {
    const type = this.type();
    return (type === 'date' || type === 'time') && !!this.placeholder() && !this._internalValue();
  });

  _toggleTimeRangePicker(event: Event): void {
    if (this.type() !== 'timeRange' || this.disabledState() || this.readonly()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (this._timeRangePickerOpen()) {
      this._closeTimeRangePicker();
    } else {
      this._openTimeRangePicker();
    }
  }

  _onTimeRangeStartChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this._timeRangeStart.set(target.value);
  }

  _onTimeRangeEndChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this._timeRangeEnd.set(target.value);
  }

  _applyTimeRange(): void {
    const start = this._timeRangeStart();
    const end = this._timeRangeEnd();

    if (!start || !end) {
      return;
    }

    const formatted = `${this._formatTwentyFourHourToTwelveHour(start)} - ${this._formatTwentyFourHourToTwelveHour(end)}`;
    const previousValue = this._internalValue();

    this._internalValue.set(formatted);
    this._internalControl.setValue(formatted);
    this._onChange(formatted);
    this._validationState.markDirty();
    this._syncShowErrors();
    this._closeTimeRangePicker();

    this.valueChange.emit({
      value: formatted,
      previousValue,
      source: this,
    });
  }

  _clearTimeRange(): void {
    const previousValue = this._internalValue();
    this._timeRangeStart.set('');
    this._timeRangeEnd.set('');
    this._internalValue.set('');
    this._internalControl.setValue('');
    this._onChange('');
    this._validationState.markDirty();
    this._syncShowErrors();
    this._closeTimeRangePicker();

    this.valueChange.emit({
      value: '',
      previousValue,
      source: this,
    });
  }

  /**
   * Syncs the _showErrors signal with the validation state. Called after any validation state change to ensure reactive
   * updates.
   */
  private _syncShowErrors(): void {
    this._showErrors.set(this._validationState.showErrors());
  }

  private _openTimeRangePicker(): void {
    if (this._overlayRef) return;

    const hostWidth = this._hostElement.nativeElement.getBoundingClientRect().width;
    const positions: ConnectedPosition[] = [
      { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
      { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
    ];

    const config = new OverlayConfig({
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._hostElement)
        .withPositions(positions)
        .withPush(false),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this._overlay.scrollStrategies.close(),
      width: Math.max(hostWidth, 256),
      minWidth: 256,
    });

    this._overlayRef = this._overlay.create(config);

    const tpl = this._timeRangePickerTemplate();
    if (tpl) {
      this._overlayRef.attach(new TemplatePortal(tpl, this._viewContainerRef));
    }

    this._overlayRef.backdropClick().subscribe(() => this._closeTimeRangePicker());
    this._overlayRef.detachments().subscribe(() => {
      this._timeRangePickerOpen.set(false);
      this._overlayRef = null;
    });

    this._timeRangePickerOpen.set(true);
  }

  private _closeTimeRangePicker(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = null;
    }
    this._timeRangePickerOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  protected _handleDocumentClick(event: Event): void {
    // Overlay backdrop handles outside-click when the CDK overlay is open.
    // This handler only runs as a fallback (e.g., when overlay was not created).
    if (!this._timeRangePickerOpen() || this._overlayRef) {
      return;
    }

    const target = event.target as Node | null;
    if (!target) {
      return;
    }

    if (!this._hostElement.nativeElement.contains(target)) {
      this._closeTimeRangePicker();
    }
  }

  // --- Public Methods ---

  /** Cleanup overlay on component destroy. */
  ngOnDestroy(): void {
    this._closeTimeRangePicker();

    if (typeof window !== 'undefined') {
      window.removeEventListener(DICTATION_ENABLED_CHANGED_EVENT, this._refreshDictationFeatureFlag);
      window.removeEventListener('storage', this._refreshDictationFeatureFlag);
    }

    this._dictationController?.dispose();
    this._dictationController = null;
  }

  /** Reset the input to empty state. */
  reset(): void {
    this._internalValue.set('');
    this._validationState.reset();
    this._syncShowErrors();
  }

  /** Toggle password visibility */
  _togglePasswordVisibility(): void {
    this._showPassword.update((value) => !value);
  }

  /** Pre-warms the microphone pipeline before the user actually clicks, reducing recognition startup latency. */
  _onDictationButtonWarmUp(): void {
    HxDictationController.warmUp();
  }

  /** Handles clicks on the dictation microphone button: starts or stops the active recognition session. */
  _onDictationButtonClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.disabledState() || this.readonly()) {
      return;
    }

    if (this.dictationState() === 'listening') {
      this._dictationController?.stop();
      return;
    }

    this._dictationBaseValue = this._internalValue();
    this._dictationCommittedText = '';

    if (!this._dictationController) {
      this._dictationController = new HxDictationController({
        onInterimResult: (transcript) => this._previewDictatedText(transcript),
        onFinalResult: (transcript) => this._commitDictatedText(transcript),
        onStateChange: (state) => {
          this.dictationState.set(state);
          this._changeDetectorRef.markForCheck();
        },
        onError: () => {
          // Errors (permission denied/dismissed, no speech, network, unsupported, etc.) simply return the
          // control to idle/error state via onStateChange — existing content and normal behavior are untouched.
        },
      });
    }

    this._dictationController.start();
  }

  /** Builds `base + committed + interim`, skipping empty parts and joining with single spaces. */
  private _composeDictationValue(interim: string): string {
    return [this._dictationBaseValue.replace(/\s+$/, ''), this._dictationCommittedText, interim]
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
      .join(' ');
  }

  /** Live preview: shows in-progress (not-yet-final) speech immediately, without committing it. */
  private _previewDictatedText(interimTranscript: string): void {
    this._applyDictatedValue(this._composeDictationValue(interimTranscript));
  }

  /** Commits finalized speech permanently so it is never overwritten/duplicated by later interim previews. */
  private _commitDictatedText(finalTranscript: string): void {
    const trimmed = finalTranscript.trim();
    if (!trimmed) {
      return;
    }
    this._dictationCommittedText = [this._dictationCommittedText, trimmed].filter(Boolean).join(' ');
    this._applyDictatedValue(this._composeDictationValue('') + ' ');
  }

  /** Writes a dictation-driven value through the same change pipeline as manual typing. */
  private _applyDictatedValue(newValue: string): void {
    const element = this._textareaElement()?.nativeElement;
    if (element) {
      element.value = newValue;
      element.setSelectionRange(newValue.length, newValue.length);
    }

    this._internalValue.set(newValue);
    this._internalControl.setValue(newValue);
    this._onChange(newValue);
    this._validationState.markDirty();
    this._syncShowErrors();

    if (this.autoExpand() && element) {
      this._calculateHeight(element);
    }

    this._changeDetectorRef.markForCheck();
  }

  /** Programmatically focus the input element */
  focus(): void {
    const element = this.multiline() ? this._textareaElement() : this._inputElement();
    if (element?.nativeElement) {
      element.nativeElement.focus();
      // For text inputs, also select all text if there is any
      if (element.nativeElement.value && element.nativeElement.select) {
        element.nativeElement.select();
      }
    }
  }

  /** Programmatically blur the input element */
  blur(): void {
    const element = this.multiline() ? this._textareaElement() : this._inputElement();
    if (element?.nativeElement) {
      element.nativeElement.blur();
    }
  }

  // ============================================================================
  // VALIDATOR IMPLEMENTATION
  // ============================================================================

  override validate(control: AbstractControl): ValidationErrors | null {
    return this._internalControl.errors;
  }

  override registerOnValidatorChange(fn: () => void): void {
    this._validatorChangeFn = fn;
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  /** Builds validator array based on component inputs - optimized to use effective min/max */
  private _buildValidators(): any[] {
    const validators: any[] = [];

    if (this.requiredInput()) {
      validators.push(Validators.required);
    }

    if (this.minlength()) {
      validators.push(Validators.minLength(this.minlength()!));
    }

    if (this.maxlength()) {
      validators.push(Validators.maxLength(this.maxlength()!));
    }

    if (this.pattern()) {
      validators.push(Validators.pattern(this.pattern()!));
    }

    // Use min/max ONLY for number type inputs and only when explicitly set
    if (this.type() === 'number') {
      const minValue = this.min();
      if (minValue !== undefined && minValue !== null) {
        validators.push(Validators.min(Number(minValue)));
      }

      const maxValue = this.max();
      if (maxValue !== undefined && maxValue !== null) {
        validators.push(Validators.max(Number(maxValue)));
      }

      if (!this.allowNegatives()) {
        validators.push((control: AbstractControl) => {
          const value = control.value;
          if (value === null || value === undefined || value === '') {
            return null;
          }

          const numericValue = Number(value);
          if (!Number.isNaN(numericValue) && numericValue < 0) {
            return { negativeValue: true };
          }

          return null;
        });
      }
    }

    if (this.type() === 'email') {
      validators.push(Validators.email);
    }

    if (this.type() === 'timeRange') {
      validators.push((control: AbstractControl) => {
        const value = typeof control.value === 'string' ? control.value.trim() : '';
        if (!value) {
          return null;
        }

        const parsedRange = this._parseTimeRange(value);
        if (!parsedRange) {
          return { timeRange: true };
        }

        if (parsedRange.endMinutes < parsedRange.startMinutes) {
          return { timeRange: true };
        }

        return null;
      });
    }

    // Note: external FormControl validators are no longer pulled in here - HxInput is
    // registered under NG_VALIDATORS, so Angular composes this control's validate() directly
    // onto the external control. Pulling the external control's validator in here as well
    // would create a circular reference (external -> validate() -> internal, and internal ->
    // external validator -> validate() again) that reads stale internal errors.

    return validators;
  }

  /**
   * Calculates and sets the height of the textarea for auto-expand mode. Uses scrollHeight to determine content height
   * and respects minRows/maxRows constraints.
   *
   * @private
   * @param textarea The textarea element to resize
   */
  private _calculateHeight(textarea: HTMLTextAreaElement): void {
    if (!this.autoExpand()) return;

    // Reset height to allow shrinking
    textarea.style.height = 'auto';

    // Get the computed line height
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight);

    // Calculate height based on scrollHeight
    const scrollHeight = textarea.scrollHeight;

    // Calculate min and max heights based on rows
    const minHeight = this.minRows() * lineHeight;
    const maxHeight = this.maxRows() ? this.maxRows()! * lineHeight : Infinity;

    // Apply constrained height
    const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));
    textarea.style.height = `${newHeight}px`;

    // Show scrollbar if content exceeds maxRows
    if (this.maxRows() && scrollHeight > maxHeight) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  }

  /** Parse supported time range formats and return comparable minute values. */
  private _parseTimeRange(value: string): { startMinutes: number; endMinutes: number } | null {
    const normalized = value.trim();

    // 24-hour format: HH:mm - HH:mm
    const twentyFourHourMatch = /^([01]\d|2[0-3]):([0-5]\d)\s*-\s*([01]\d|2[0-3]):([0-5]\d)$/.exec(normalized);
    if (twentyFourHourMatch) {
      const startMinutes = Number(twentyFourHourMatch[1]) * 60 + Number(twentyFourHourMatch[2]);
      const endMinutes = Number(twentyFourHourMatch[3]) * 60 + Number(twentyFourHourMatch[4]);
      return { startMinutes, endMinutes };
    }

    // 12-hour format: h:mm AM - h:mm PM
    const twelveHourMatch = /^(0?[1-9]|1[0-2]):([0-5]\d)\s*(AM|PM)\s*-\s*(0?[1-9]|1[0-2]):([0-5]\d)\s*(AM|PM)$/i.exec(
      normalized
    );
    if (twelveHourMatch) {
      const startMinutes = this._toMinutesFromTwelveHour(
        Number(twelveHourMatch[1]),
        Number(twelveHourMatch[2]),
        twelveHourMatch[3] ?? ''
      );
      const endMinutes = this._toMinutesFromTwelveHour(
        Number(twelveHourMatch[4]),
        Number(twelveHourMatch[5]),
        twelveHourMatch[6] ?? ''
      );
      return { startMinutes, endMinutes };
    }

    return null;
  }

  private _toMinutesFromTwelveHour(hours: number, minutes: number, meridiem: string): number {
    const normalizedMeridiem = meridiem.toUpperCase();
    const normalizedHours = hours % 12;
    const twentyFourHourHours = normalizedMeridiem === 'PM' ? normalizedHours + 12 : normalizedHours;
    return twentyFourHourHours * 60 + minutes;
  }

  private _minutesToTwentyFourHour(totalMinutes: number): string {
    const normalized = ((totalMinutes % 1440) + 1440) % 1440;
    const hours = Math.floor(normalized / 60)
      .toString()
      .padStart(2, '0');
    const minutes = (normalized % 60).toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private _formatTwentyFourHourToTwelveHour(value: string): string {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
    if (!match) {
      return value;
    }

    const hours24 = Number(match[1]);
    const minutes = match[2];
    const meridiem = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    return `${hours12.toString().padStart(2, '0')}:${minutes} ${meridiem}`;
  }

  /**
   * Gets error message for display based on validation error type. Uses unified validation state as the single source
   * of truth.
   *
   * @param errorType The type of validation error
   * @returns The error message string
   */
  protected _getErrorMessage(errorType: string): string {
    const errors = this._validationState.errors();
    if (!errors) return '';

    switch (errorType) {
      case 'required':
        return this.requiredErrorMessage() || 'This field is required';

      case 'minlength':
        if (this.minlengthErrorMessage()) return this.minlengthErrorMessage();
        return `Minimum length is ${errors['minlength']?.requiredLength} characters`;

      case 'maxlength':
        if (this.maxlengthErrorMessage()) return this.maxlengthErrorMessage();
        return `Maximum length is ${errors['maxlength']?.requiredLength} characters`;

      case 'pattern':
        return this.patternErrorMessage() || 'Invalid format';

      case 'min':
        if (this.minErrorMessage()) return this.minErrorMessage();
        return `Minimum value is ${errors['min']?.min}`;

      case 'max':
        if (this.maxErrorMessage()) return this.maxErrorMessage();
        return `Maximum value is ${errors['max']?.max}`;

      case 'negativeValue':
        return this.negativeValueErrorMessage() || 'Negative values are not allowed';

      case 'email':
        return this.emailErrorMessage() || 'Invalid email address';

      case 'alphabetical':
        return this.alphabeticalErrorMessage() || 'Only alphabetical characters (a-z, A-Z) are allowed';

      case 'numerical':
        return this.numericalErrorMessage() || 'Only numbers (0-9) are allowed';

      case 'specialCharacter':
        return this.specialCharacterErrorMessage() || 'Invalid characters detected';

      case 'timeRange':
        return this.timeRangeErrorMessage() || 'Invalid time range';

      default:
        return '';
    }
  }
}
